import { ChatTransport } from "./ChatTransport";
import { streamJsonSseWithRetry } from "../../../../utils/SseStreamUtil";

export const BROWSER_SESSION_EVENTS_PATH = (baseUrl, id) =>
  `${baseUrl}/ai/agent/session/${id}/events`;
export const BROWSER_SESSION_STREAM_PATH = (baseUrl, id, after) =>
  `${baseUrl}/ai/agent/session/${id}/stream?after=${after}`;
export const PUBLIC_CHAT_EVENTS_PATH = (baseUrl, token) =>
  `${baseUrl}/public/ai/agent/public-chat/session/${token}/events`;
export const PUBLIC_CHAT_STREAM_PATH = (baseUrl, token, after) =>
  `${baseUrl}/public/ai/agent/public-chat/session/${token}/stream?after=${after}`;

export class DefaultChatTransport extends ChatTransport {
  constructor({ baseUrl, onEvent, onConnectionStateChange, onStreamDiagnostic, skipAuth = false }) {
    super();
    this._baseUrl = baseUrl;
    this._onEvent = onEvent;
    this._onConnectionStateChange = onConnectionStateChange;
    this._onStreamDiagnostic = onStreamDiagnostic;
    this._skipAuth = skipAuth;
    this._abortController = null;
    this._lastEventId = 0;
    this._connectId = null;
  }

  async connect(id, options = {}) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = 0;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_CHAT_EVENTS_PATH(this._baseUrl, id)
      : BROWSER_SESSION_EVENTS_PATH(this._baseUrl, id);
    return this._stream(url, options);
  }

  async resume(id, lastEventId) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = lastEventId || this._lastEventId;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_CHAT_STREAM_PATH(this._baseUrl, id, this._lastEventId)
      : BROWSER_SESSION_STREAM_PATH(this._baseUrl, id, this._lastEventId);
    return this._stream(url, {});
  }

  disconnect() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._connectId = null;
  }

  get lastEventId() {
    return this._lastEventId;
  }

  async _stream(url, options) {
    const { shouldReconnect, maxRetries = 5 } = options;

    return streamJsonSseWithRetry({
      url,
      abortController: this._abortController,
      maxRetries,
      logLabel: this._skipAuth ? "PublicChatTransport SSE" : "ChatTransport SSE",
      skipAuth: this._skipAuth,
      onEvent: (eventName, data, eventId) => {
        const parsed = eventId != null ? Number(eventId) : NaN;
        const seq = Number.isFinite(parsed) ? parsed : null;
        if (seq != null && seq > this._lastEventId) {
          this._lastEventId = seq;
        }
        if (this._onEvent) {
          // Forward the backend-minted monotonic seq (SSE `id:`) so the store
          // can sequence-gate lifecycle transitions. Previously dropped here.
          this._onEvent(eventName, data, seq);
        }
      },
      shouldReconnect: () => {
        return typeof shouldReconnect === "function" ? shouldReconnect() : false;
      },
      onConnectionStateChange: (state) => {
        if (this._onConnectionStateChange) this._onConnectionStateChange(state);
      },
      onStreamDiagnostic: this._onStreamDiagnostic,
      getReconnectUrl: (lastEventId) => {
        if (lastEventId > 0 && this._connectId) {
          return this._skipAuth
            ? PUBLIC_CHAT_STREAM_PATH(this._baseUrl, this._connectId, lastEventId)
            : BROWSER_SESSION_STREAM_PATH(this._baseUrl, this._connectId, lastEventId);
        }
        return null;
      },
    });
  }
}
